/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { VisInvalidDataError } from '#UNSAFE_visErrors';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { useCssVars } from '#hooks/PRIVATE_useCssVars/useCssVars';
import { styles } from './themes/ChartStyles.css';
import { useMemo, useRef, useState, useImperativeHandle } from 'preact/hooks';
import { PlotArea } from '#PRIVATE_PlotArea';
import { useId } from '#hooks/UNSAFE_useId';
import { Axis } from '#PRIVATE_Axis/Axis';
import { useChartViewport } from '#hooks/PRIVATE_useChartViewport';
import {
  getAvailSpace,
  getLayoutInfo,
  getOuterGaps,
  getScrollBarSpace,
  updateScrollBarSpace
} from '#utils/PRIVATE_chartUtils/layoutUtils';
import {
  GroupAxisProps,
  DataAxisProps,
  TimeAxisProps,
  SizeHandle,
  Info
} from '#utils/UNSAFE_visTypes/chart';
import { useChartNav } from '#hooks/PRIVATE_useChartNav/useChartNav';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { getAxesPosition } from '#PRIVATE_Axis/utils/axisUtils';
import { ScrollBar } from '#PRIVATE_ChartScrollBar';
import { useSelectDrill } from '#hooks/PRIVATE_useChartEvents/useSelectDrill';
import { useChartDatatipAndCursor } from '#hooks/PRIVATE_useChartEvents/useDatatipAndCursor';
import { getGroupNavUtil } from '#PRIVATE_Axis/utils/axisNavUtils';
import { Gridlines } from '#PRIVATE_PlotArea/Gridlines';
import {
  buildNavigableRefObjects,
  getRefObjectBounds as computeRefObjectBounds
} from '#utils/PRIVATE_chartUtils/refObjectsNavUtils';
import { useUser } from '#hooks/UNSAFE_useUser';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { getChartVars } from '#utils/PRIVATE_chartUtils';
import { useChartContextMenu } from '#hooks/PRIVATE_useChartContextMenu';
import { useChartDnd } from '#hooks/PRIVATE_useChartDnd';
import { getElementData } from '#utils/UNSAFE_visUtils';
import { useTextTruncation } from '#PRIVATE_VisSVGText/useTextTruncation';
import { useChartMarquee } from '#hooks/PRIVATE_useChartMarquee';
import { ChartProps } from './Chart';
import type { gestureTypes } from '#hooks/PRIVATE_useVisContextMenu';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { LineAreaItem } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';
import { BarItemData } from '#UNSAFE_BarChart/barChart.types';
import { OverviewContext } from '#hooks/PRIVATE_useOverviewContext';
import { getWindowCoords } from '#PRIVATE_VisOverview/utils';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { getRenderer } from './overviewUtils';
import { ComponentChildren, Ref, VNode } from 'preact';
import { forwardRef } from 'preact/compat';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import {
  getAriaLabelGenerator,
  getTextAndBoundsUtil,
  getTextAndDimsInfo
} from '#utils/PRIVATE_chartUtils/dataTipUtils';
import { hasData, hasInvalidData } from '#utils/PRIVATE_chartUtils/dataUtils';
import { ReferenceObjects } from '#PRIVATE_PlotArea/ReferenceObjects';
import { getActiveInfo } from '#utils/PRIVATE_chartUtils/utils';
import { merge } from '#utils/UNSAFE_stringUtils';

type ChartWithDimensionsProps<K, D> = Omit<ChartProps<K, D>, 'width' | 'height'> & {
  width: number;
  height: number;
  overviewRenderer: (props: Dimension) => {
    background: ComponentChildren;
    slidingWindow: ComponentChildren;
  };
  yAxisSize: number;
  isOverview: boolean;
};

const OVERVIEW_GAP = 10;

const chartVars = getChartVars();

type ForwardRefChart = <
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
>(
  props: ChartWithDimensionsProps<K, D> & {
    ref?: Ref<SizeHandle>;
  }
) => VNode | null;

export const ChartWithDimensions = forwardRef(
  <K extends string | number, D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>>(
    {
      width,
      height,
      selectedIds,
      hiddenIds,
      hideAndShowBehavior = 'none',
      orientation = 'vertical',
      xAxis,
      yAxis,
      y2Axis,
      plotArea,
      dragMode = 'user',
      series,
      groups,
      isStacked = false,
      stackLabelStyle,
      getStackGroup,
      getDataItem,
      selectionMode = 'none',
      drilling = 'off',
      dataCursorPosition,
      onSelectionChange,
      isDataCursorEnabled,
      dataCursorStyle,
      onViewportChange,
      onDataCursorPositionChange,
      onItemDrill,
      onGroupDrill,
      onItemHover,
      onItemFocus,
      datatip,
      zoomAndScroll = 'off',
      getNavUtil,
      getDataItemPos,
      getGapRatio,
      valueFormats,
      findNearest,
      getMarkerInfo,
      isRtl,
      children,
      testId,
      renderGridLinesInFront,
      isDataXAxis,
      contextMenuConfig,
      overview = 'off',
      getStartAndEndAxesOffset,
      defaultOverviewContentRenderer,
      isOverview,
      yAxisSize,
      overviewRenderer,
      initialZooming = 'none',
      ...props
    }: ChartWithDimensionsProps<K, D>,
    ref: Ref<SizeHandle> = null
  ) => {
    const timeAxisType = (xAxis as TimeAxisProps)?.timeAxisType;
    const isTimeAxis = timeAxisType != null;
    if (hasInvalidData(timeAxisType, series, groups, getDataItem)) {
      throw new VisInvalidDataError('Chart received invalid data');
    }

    const testIdProps = useTestId(testId);
    const rootRef = useRef<HTMLDivElement>(null);
    const isTestEnv = testIdProps['data-testid'] !== undefined;
    const isHoriz = orientation === 'horizontal';
    const { xAxisPosition, yAxisPosition, y2AxisPosition } = getAxesPosition(
      yAxis,
      y2Axis,
      isHoriz,
      isRtl
    );
    const { resolvedVars, cssContent } = useCssVars(chartVars);
    const resolvedColors = Array.from(Array(12)).map((_, index) => {
      return resolvedVars[`color${index + 1}`];
    });
    const { textMeasureContent, getTextDimensions } = useTextDimensions();
    let availSpace = getAvailSpace(width, height);

    if (isOverview && yAxisSize) {
      const w = availSpace.width - yAxisSize;
      const gaps = getOuterGaps(w, height);
      availSpace.width -= yAxisSize - 2 * gaps.width;
      availSpace.x += yAxisSize - gaps.width;
    }

    const updatedSpace = getScrollBarSpace(
      isTimeAxis,
      availSpace,
      zoomAndScroll,
      xAxisPosition,
      overview
    );
    let scrollDims = updatedSpace.scrollDims;
    availSpace = updatedSpace.availSpace;
    const { locale } = useUser();
    const { x, y, width: _width, height: _height } = availSpace;
    const labelFontSize = resolvedVars['labelFontSize'];

    const split = y2Axis?.split ?? 'off';
    const isSplitDualY = split !== 'off';
    const splitterPosition = split !== 'off' ? split : 0.5;

    const layoutInfo = useMemo(() => {
      const hiddenSet = new Set(hiddenIds);
      return getLayoutInfo<K, D>(
        series,
        groups,
        getDataItem,
        xAxis,
        yAxis,
        y2Axis,
        { x, y, width: _width, height: _height }, // availSpace
        hiddenSet,
        hideAndShowBehavior,
        isHoriz,
        isRtl,
        isStacked,
        labelFontSize,
        getStartAndEndAxesOffset,
        locale,
        isSplitDualY,
        splitterPosition,
        getStackGroup,
        getTextDimensions,
        isTestEnv,
        isDataXAxis,
        initialZooming,
        zoomAndScroll
      );
    }, [
      x,
      y,
      _width,
      _height,
      getDataItem,
      getTextDimensions,
      groups,
      hiddenIds,
      hideAndShowBehavior,
      isDataXAxis,
      isHoriz,
      isRtl,
      isStacked,
      isTestEnv,
      locale,
      getStartAndEndAxesOffset,
      labelFontSize,
      series,
      xAxis,
      yAxis,
      y2Axis,
      splitterPosition,
      isSplitDualY,
      getStackGroup,
      initialZooming,
      zoomAndScroll
    ]);

    const {
      getGroupFromPath,
      levelsArray,
      xAxisInfo,
      xProps,
      xScale,
      yProps,
      y2Props,
      yScale,
      y2Scale,
      yMaxDim,
      yAxisInfo,
      plotAreaSpace,
      xSpace,
      ySpace,
      y2Space,
      idToDataMap,
      y2AxisInfo,
      getYPreferredSize,
      getY2PreferredSize,
      getXPreferredSize
    } = layoutInfo;

    useImperativeHandle(ref!, () => ({
      _getYaxis: () => {
        return { getPreferredSize: getYPreferredSize, bounds: ySpace };
      },
      _getY2axis: () => {
        return { getPreferredSize: getY2PreferredSize, bounds: y2Space };
      },
      _getXaxis: () => {
        return { getPreferredSize: getXPreferredSize, bounds: xSpace };
      }
    }));

    const leafGroupsInfo = levelsArray.length > 0 ? levelsArray[levelsArray.length - 1] : [];

    scrollDims = updateScrollBarSpace(scrollDims, xAxisPosition === 'bottom', isRtl, yMaxDim);

    const gapRatio = getGapRatio(xAxisInfo.startIndex!, xAxisInfo.endIndex!);

    const getNextChartItem = getNavUtil(xAxisInfo.startIndex!, xAxisInfo.endIndex!);

    const { getNextNavGroup, getDefaultNavGroup } = getGroupNavUtil(
      levelsArray,
      drilling,
      isRtl,
      isHoriz,
      xProps.isGroupLabelRendered
    );

    const navigableRefObjects = useMemo(
      () =>
        buildNavigableRefObjects(
          xAxis?.referenceObjects,
          yAxis?.referenceObjects,
          y2Axis?.referenceObjects
        ),
      [xAxis?.referenceObjects, yAxis?.referenceObjects, y2Axis?.referenceObjects]
    );

    const getNextRefObject = (
      info: Info,
      arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
    ): Info => {
      const list = navigableRefObjects;
      if (!list || list.length === 0) {
        return {};
      }
      const dir = arrow === 'ArrowLeft' || arrow === 'ArrowUp' ? -1 : 1;

      const idx =
        info.refObjId != null
          ? list.findIndex((o) => o.id === info.refObjId)
          : dir > 0
          ? -1
          : list.length;

      // Move one step in the desired direction. Clamp at the ends.
      let nextIdx = idx + dir;
      if (nextIdx < 0) nextIdx = 0;
      if (nextIdx >= list.length) nextIdx = list.length - 1;

      return { refObjId: list[nextIdx].id };
    };

    const {
      ignoreSelectPointerUp,
      cursor,
      yAxisCursor,
      marqueeProps,
      marqueeContent,
      toggleButtonContent,
      isPointInsideMarquee,
      dragMode: mode
    } = useChartMarquee<K, D>({
      rootRef,
      plotAreaSpace,
      xSpace,
      ySpace,
      dragMode,
      chartWidth: width,
      isMultiSelection: selectionMode === 'multiple',
      onViewportChange,
      isZoomScroll: zoomAndScroll === 'live',
      xScale,
      selectedIds,
      getDataById: (id) => idToDataMap.get(id),
      onSelectionChange,
      isRtl,
      xAxisInfo
    });

    const { touchResponse, touchResponseStyle } = useVisTouchResponse({
      type: 'auto',
      supportsTouchDragGestures: mode !== 'off',
      rootRef
    });

    const {
      activeId,
      focusedItemInfo,
      hoveredItemInfo,
      focusedGroupInfo,
      navProps,
      onFocusUpdate
    } = useChartNav({
      rootRef,
      getNextChartItem,
      getNextRefObject,
      getNextNavGroup,
      getDefaultNavGroup,
      touchResponse,
      onItemHover,
      onItemFocus,
      drilling,
      xAxisInfo
    });

    const onContextMenuDismissed = (gesture: gestureTypes) => {
      if (gesture === 'keyboard') {
        onFocusUpdate();
      }
    };

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

    const getTextAndBounds = (target: EventTarget | null) => {
      return getTextAndBoundsUtil(
        target,
        rootRef,
        xAxis?.title,
        yAxis?.title,
        y2Axis?.title,
        xProps.titleProps?.dims,
        yProps.titleProps?.dims,
        y2Props.titleProps?.dims,
        xProps.tickLabels,
        focusedGroupInfo
      );
    };

    const dimsAndTextInfo = getTextAndDimsInfo(
      xProps.tickLabels,
      focusedGroupInfo.groupIndex,
      focusedGroupInfo.levelIndex
    );
    const { tooltipContent, tooltipProps } = useTextTruncation({
      getTextAndBounds,
      width,
      isRtl,
      textAndBounds: {
        bounds: dimsAndTextInfo?.dims,
        text: dimsAndTextInfo?.text
      }
    });

    const viewportProps = useChartViewport({
      rootRef,
      width,
      height,
      xAxisInfo,
      yAxisInfo,
      y2AxisInfo,
      xScale,
      yScale,
      y2Scale,
      plotAreaSpace,
      isRtl,
      isHoriz,
      zoomAndScroll,
      onViewportChange
    });

    const ignoreAsTrigger = (target: EventTarget | null) => {
      if (!target) {
        return false;
      }

      const dataset = getElementData(rootRef, target as HTMLElement);
      if (
        (dataset &&
          (dataset['ojPrivateObject'] === 'marquee' ||
            dataset['ojPrivateObject'] === 'dragModeControls' ||
            dataset['ojPrivateObject'] === 'scrollBar')) ||
        ignoreSelectPointerUp
      ) {
        return true;
      }

      return false;
    };

    const { selectDrillProps, selectionContent } = useSelectDrill<K, D>({
      selectionMode,
      selection: [...selectedIds],
      onChange: onSelectionChange,
      rootRef,
      drilling,
      onItemDrill,
      onGroupDrill,
      focusedGroupInfo,
      focusedItemInfo,
      levelsArray,
      getDataItem,
      getDataById: (id) => idToDataMap.get(id),
      ignoreAsTrigger
    });

    const findNearestData = findNearest(
      xScale as any,
      yScale,
      y2Scale,
      xProps.axisStepWidth!,
      xProps.averageGroupZ!,
      gapRatio
    );
    const getDataItemPosition = getDataItemPos(
      xScale as any,
      yScale,
      y2Scale,
      xProps.axisStepWidth!,
      xProps.averageGroupZ!,
      gapRatio
    );
    const [datatipDisabled, setDatatipDisabled] = useState(false);

    const getItemAriaLabel = getAriaLabelGenerator(
      series,
      translations,
      valueFormats,
      leafGroupsInfo,
      xAxis,
      !!isDataXAxis,
      getGroupFromPath,
      xProps.defaultLabelFormatter,
      yProps.defaultLabelFormatter,
      y2Props.defaultLabelFormatter
    );

    const getRefItem = (id: number | string) => {
      return (
        xAxis?.referenceObjects?.find((item) => item.id === id) ??
        yAxis?.referenceObjects?.find((item) => item.id === id) ??
        y2Axis?.referenceObjects?.find((item) => item.id === id)
      );
    };

    const getRefObjectBounds = (id: string): Dimension | undefined => {
      return computeRefObjectBounds(
        id,
        xAxis?.referenceObjects,
        yAxis?.referenceObjects,
        y2Axis?.referenceObjects,
        xScale as any,
        yScale,
        y2Scale,
        orientation as 'horizontal' | 'vertical',
        plotAreaSpace
      );
    };

    const getRefAxis = (id: string): 'x' | 'y' | 'y2' | undefined => {
      if (xAxis?.referenceObjects?.some((item) => item.id === id)) return 'x';
      if (yAxis?.referenceObjects?.some((item) => item.id === id)) return 'y';
      if (y2Axis?.referenceObjects?.some((item) => item.id === id)) return 'y2';
      return undefined;
    };

    const {
      datatipContent,
      dataCursorContent,
      datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
    } = useChartDatatipAndCursor<K, D>({
      series,
      leafGroupsInfo,
      width,
      height,
      findNearest: findNearestData,
      plotAreaSpace,
      onDataCursorPositionChange,
      isDataCursorEnabled,
      dataCursorPosition,
      dataCursorStyle,
      hoveredItemInfo,
      focusedItemInfo,
      getDataItem,
      getRefItem,
      getRefObjectBounds,
      getRefAxis,
      getMarkerInfo,
      getDataItemPos: getDataItemPosition,
      datatip,
      valueFormats,
      isHoriz,
      touchResponse,
      isDisabled: datatipDisabled,
      xScale: xScale as any,
      yScale,
      y2Scale,
      timeAxisType,
      defaultXFormatter: xProps.defaultLabelFormatter,
      defaultYFormatter: yProps.defaultLabelFormatter,
      defaultY2Formatter: y2Props.defaultLabelFormatter,
      hasXYValues: isDataXAxis,
      getGroupFromPath,
      translations
    });

    const isGroupInteractive = (index: number) => {
      if (isTimeAxis) {
        return false;
      }
      const groupDrill = groups[index]?.drilling;
      if (groupDrill === 'on') {
        return true;
      }

      if (groupDrill != 'off' && (drilling === 'on' || drilling === 'groupsOnly')) {
        return true;
      }
      return false;
    };

    const plotAreaClipPathId = useId();

    //  const { yTicks, y2Ticks } = getYY2Ticks(yAxisInfo.ticks, y2AxisInfo.ticks, isHoriz, isSplitDualY);
    const gridLines = (
      <Gridlines
        plotArea={plotArea}
        availSpace={plotAreaSpace}
        xAxisPosition={xAxisPosition}
        isRtl={isRtl}
        yMinorTicks={yAxisInfo.minorTicks}
        xMinorTicks={xAxisInfo.minorTicks}
        xScale={xScale}
        yScale={yScale}
        y2Scale={y2Scale}
        isLog={yAxis?.scale === 'log'}
        yAxisPosition={yAxisPosition}
        isDataXAxis={isDataXAxis}
        xAxis={xAxis as DataAxisProps}
        yAxis={yAxis}
        y2Axis={y2Axis}
        hasAxisLine={!isOverview}
        y2MinorTicks={y2AxisInfo?.minorTicks}
        y2Ticks={y2AxisInfo.ticks}
        yTicks={yAxisInfo.ticks}
        xTicks={xAxisInfo.ticks}
        isSplitDualY={isSplitDualY}
      />
    );

    const { contextMenuContent, contextMenuProps } = useChartContextMenu<K, D>({
      isRtl,
      width,
      height,
      getDataItemPosition,
      series,
      levelsArray,
      focusedGroupInfo,
      getDataItem,
      focusedItemInfo,
      rootRef,
      xAxisTitle: xAxis?.title,
      yAxisTitle: yAxis?.title,
      contextMenuConfig: hasData(series, groups) ? contextMenuConfig : undefined,
      onContextMenuDismissed
    });

    const [dragOver, setDragOver] = useState<{ accepted: boolean | undefined; type: string }>({
      accepted: undefined,
      type: ''
    });

    const { draggable, dndProps, dndContent } = useChartDnd<K, D>({
      onDrag: props.onDrag,
      onDragStart: props.onDragStart,
      onDragEnd: props.onDragEnd,
      onDragEnter: props.onDragEnter,
      onDragLeave: props.onDragLeave,
      onDragOver: props.onDragOver,
      onDrop: props.onDrop,
      itemsDraggable: props.itemsDraggable,
      groupDraggable: props.groupDraggable,
      rootRef,
      levelsArray,
      series,
      selectedIds,
      getDataItem,
      xSpace,
      ySpace,
      plotAreaSpace,
      setDragOver,
      setDatatipDisabled,
      onSelectionChange
    });

    const eventsProps = mergeProps(
      navProps,
      selectDrillProps,
      viewportProps,
      datatipProps,
      marqueeProps,
      tooltipProps,
      contextMenuProps,
      dndProps
    );

    const overviewContentRenderer =
      overview != 'off' && (overview === 'on' || !overview.renderer)
        ? defaultOverviewContentRenderer
        : undefined;

    const { background, slidingWindow } = overviewRenderer(plotAreaSpace);

    const isActiveInfoRef = focusedItemInfo.refObjId != null && focusedItemInfo.isFocusVisible;
    const activeInfo = getActiveInfo(focusedItemInfo, hoveredItemInfo);
    return (
      <>
        {getTextDimensions && resolvedVars['labelFontSize'] != undefined && (
          <div
            tabIndex={isOverview ? undefined : 0}
            draggable={draggable}
            ref={rootRef}
            aria-activedescendant={activeId}
            aria-label={props['aria-label']}
            aria-labelledby={props['aria-labelledby']}
            aria-describedby={merge([props['aria-describedby'], datatipAriaDescribedby])}
            style={{ ...touchResponseStyle }}
            role={isOverview ? undefined : 'application'}
            className={styles.baseChartStyle}
            data-oj-private-selection-mode={isTestEnv ? selectionMode : undefined}
            {...testIdProps}
            {...(isOverview ? {} : eventsProps)}>
            <svg
              width="100%"
              height={
                scrollDims.height != 0 && overview != 'off'
                  ? `${Math.ceil((updatedSpace.availSpace.height + OVERVIEW_GAP) * 100) / height}%`
                  : '100%'
              }>
              <clipPath id={`${plotAreaClipPathId}`}>
                <rect {...plotAreaSpace}></rect>
              </clipPath>
              {background}
              <PlotArea
                color={plotArea?.color}
                dragOverStyle={
                  dragOver.type !== 'plotArea' ? 'none' : dragOver.accepted ? 'accept' : 'reject'
                }
                cursor={cursor}
                availSpace={plotAreaSpace}
              />
              {!renderGridLinesInFront && gridLines}
              <Axis
                {...(yProps as any)}
                axisPosition={yAxisPosition}
                isRtl={isRtl}
                cursor={yAxisCursor}
                availSpace={ySpace}
                dragOverStyle={
                  dragOver.type !== 'yAxis' ? 'none' : dragOver.accepted ? 'accept' : 'reject'
                }
              />
              <Axis
                {...(y2Props as any)}
                axisPosition={y2AxisPosition}
                isRtl={isRtl}
                cursor={yAxisCursor}
                availSpace={y2Space}
                dragOverStyle={
                  dragOver.type !== 'y2Axis' ? 'none' : dragOver.accepted ? 'accept' : 'reject'
                }
              />
              <Axis
                {...xProps}
                isRtl={isRtl}
                axisPosition={xAxisPosition}
                isTextInteractive={isGroupInteractive}
                activeId={activeId}
                cursor={cursor}
                focusedGroupIndex={
                  focusedGroupInfo.isFocusVisible ? focusedGroupInfo.groupIndex : undefined
                }
                focusedGroupLevelIndex={
                  focusedGroupInfo.isFocusVisible ? focusedGroupInfo.levelIndex : undefined
                }
                availSpace={xSpace}
                dragOverStyle={
                  dragOver.type !== 'xAxis' ? 'none' : dragOver.accepted ? 'accept' : 'reject'
                }
                separatorColor={(xAxis as GroupAxisProps)?.groupSeparators?.color}
              />
              <g clip-path={`url(#${plotAreaClipPathId})`}>
                <ReferenceObjects
                  location="back"
                  activeId={isActiveInfoRef ? activeId : undefined}
                  activeInfo={activeInfo}
                  xAxis={xAxis}
                  yAxis={yAxis}
                  y2Axis={y2Axis}
                  xScale={xScale}
                  yScale={yScale}
                  y2Scale={y2Scale}
                  orientation={orientation}
                  xAxisInfo={xAxisInfo}
                  groups={groups}
                />
                {children({
                  xStartIndex: xAxisInfo.startIndex!,
                  xEndIndex: xAxisInfo.endIndex!,
                  getItemAriaLabel,
                  groupsInfo: leafGroupsInfo,
                  resolvedColors,
                  xScale: xScale as any,
                  yScale,
                  y2Scale,
                  isPointInsideMarquee,
                  defaultFontSize: resolvedVars['labelFontSize'],
                  gapRatio,
                  activeId,
                  focusedItemInfo,
                  hoveredItemInfo,
                  getTextDimensions,
                  axisStepWidth: xProps.axisStepWidth!,
                  averageGroupZ: xProps.averageGroupZ!,
                  plotAreaClipPathId
                })}
                {renderGridLinesInFront && gridLines}
                <ReferenceObjects
                  location="front"
                  xAxis={xAxis}
                  yAxis={yAxis}
                  y2Axis={y2Axis}
                  activeId={activeId}
                  activeInfo={activeInfo}
                  xScale={xScale}
                  yScale={yScale}
                  y2Scale={y2Scale}
                  orientation={orientation}
                  xAxisInfo={xAxisInfo}
                  groups={groups}
                />
                {dataCursorContent}
              </g>
              {slidingWindow}
              {zoomAndScroll === 'live' && !isOverview && (
                <ScrollBar
                  availSpace={scrollDims}
                  isRtl={isRtl}
                  viewport={xAxisInfo}
                  axisExtent={xAxisInfo}
                  isHoriz={xAxisPosition === 'bottom'}
                />
              )}
              {marqueeContent}
            </svg>
            <OverviewContext.Provider
              value={{
                yAxisSize: ySpace.width,
                isOverview: true,
                width,
                height: scrollDims.height,
                overviewRenderer: (overviewChartPlotArea: Dimension) => {
                  const { xScale: overviewXScale, ...windowCoords } = getWindowCoords(
                    isRtl,
                    xAxisInfo,
                    xAxisInfo,
                    yAxisInfo,
                    yAxisInfo,
                    overviewChartPlotArea
                  );
                  return getRenderer(
                    xAxisInfo,
                    isRtl,
                    overviewXScale,
                    overviewChartPlotArea,
                    windowCoords,
                    true,
                    onViewportChange
                  );
                }
              }}>
              {!isOverview && overview !== 'off' && overviewContentRenderer && (
                <div style={{ width, height: scrollDims.height }}>{overviewContentRenderer()}</div>
              )}
            </OverviewContext.Provider>
            {toggleButtonContent}
            {datatipContent}
            {tooltipContent}
            {contextMenuContent}
            {selectionContent}
            {dndContent}
          </div>
        )}
        {textMeasureContent}
        {cssContent}
      </>
    );
  }
) as ForwardRefChart;
